import React from 'react';
import { styled } from '@mui/system';
import Divider from "@mui/material/Divider";
import * as dates from '@simplybenefits/sb-dates';
import {
    Theme,
    Text,
    Button,
    i18n,
    tokens,
    localizationMaps
} from '@simplybenefits/sb-ui-library';

const ChangeRequestRow = styled('div')({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    height: 60,
    paddingLeft: 20,
    cursor: 'pointer',
    '&:hover': {
        filter: 'brightness(97%)'
    }
});

/**
 * A component to display a table of requested changes.
 * 
 * @param {Object} props - The component props.
 * @param {Function} props.openChangeRequest - Function to open a change request.
 * @param {Array} props.requestedChanges - List of requested changes.
 * @param {Function} props.loadMoreRequestedChanges - Function to load more requested changes.
 * @returns {JSX.Element} The rendered component.
 */
const RequestedChangesTable = ({ openChangeRequest, requestedChanges, loadMoreRequestedChanges }) => {
    const styles = {
        headerContainer: {
            height: 75,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            paddingLeft: 20
        },
        firstBlock: {
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            width: i18n.locale === 'fr' ? '30%' : '20%',
            paddingLeft: 15
        },
        centerBlock: {
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            width: '12%',
            justifyContent: 'flex-start'
        },
        lastBlock: {
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            width: i18n.locale === 'fr' ? '30%' : '20%'
        },
        buttonStyle: {
            height: 50,
            marginRight: 1.25,
            textTransform: "capitalize",
            fontSize: 16,
            alignSelf: 'flex-end'
        }
    };

    /**
     * Returns the text for the given status.
     * 
     * @param {string} status - The status of the change request.
     * @returns {string} The localized status text.
     */
    const returnStatusText = (status) => {
        if (status.toLowerCase() === 'pending member review') {
            return localizationMaps.fromEnglish('changeRequestStatuses', "Needs Review");
        }

        const lowerStatus = status.toLowerCase();

        if ([ 'incomplete', 'suspended', 'pending agent review', 'pending admin review' ].includes(lowerStatus)) {
            return i18n.t(tokens.labels.employees.pending);
        }

        return localizationMaps.fromEnglish('changeRequestStatuses', status);
    };

    /**
     * Returns the color for the given status.
     * 
     * @param {string} status - The status of the change request.
     * @returns {string} The color associated with the status.
     */
    const returnStatusColor = (status) => {
        switch (true) {
            case status.toLowerCase().includes('pending'):
                return "#FCE83A";
            case status === "Approved":
                return Theme.colors.brandGreen;
            case status === "Declined":
                return Theme.colors.red;
            default:
                return "#FCE83A";
        }
    };

    return (
        <>
            {
                requestedChanges.length
                    ? (
                        <div style={{ backgroundColor: '#FFFFFF', border: `1px solid ${ Theme.colors.lightGrey }` }}>
                            <div style={styles.headerContainer}>
                                <div style={styles.firstBlock}>
                                    <Text bold medium>{i18n.t(tokens.labels.changeRequests.requestedChange)}</Text>
                                </div>
                                <div style={styles.centerBlock}>
                                    <Text bold medium>{i18n.t(tokens.labels.changeRequests.identifier)}</Text>
                                </div>
                                <div style={styles.centerBlock}>
                                    <Text bold medium>{i18n.t(tokens.labels.changeRequests.dateSubmitted)}</Text>
                                </div>
                                <div style={styles.centerBlock}>
                                    <Text bold medium>{i18n.t(tokens.labels.admin.effectiveDate)}</Text>
                                </div>
                                <div style={styles.lastBlock}>
                                    <Text bold medium align="right">{i18n.t(tokens.titles.status)}</Text>
                                </div>
                            </div>

                            <Divider />

                            {
                                requestedChanges.map((changeRequestData, i) => (
                                    <React.Fragment key={changeRequestData.changeRequestID}>
                                        <ChangeRequestRow onClick={() => openChangeRequest(changeRequestData)}>
                                            <div style={styles.firstBlock}>
                                                <Text medium bold>{localizationMaps.fromEnglish("changeRequestTypes", changeRequestData.type)}</Text>
                                            </div>
                                            <div style={styles.centerBlock}>
                                                <Text medium>{changeRequestData.changeRequestID}</Text>
                                            </div>
                                            <div style={styles.centerBlock}>
                                                <Text medium>{dates.formatDateForDisplay(changeRequestData.submittedDate, 'MMM DD, YYYY', i18n.locale)}</Text>
                                            </div>
                                            <div style={{ ...styles.centerBlock, ...(!changeRequestData.effectiveDate ? { paddingLeft: 50 } : {}) }}>
                                                <Text medium>{changeRequestData.effectiveDate ? dates.formatDateForDisplay(changeRequestData.effectiveDate, 'MMM DD, YYYY', i18n.locale) : "-"}</Text>
                                            </div>
                                            <div style={{ ...styles.lastBlock, flexDirection: "row", justifyContent: 'flex-start' }}>
                                                <Text customStyle={{ fontSize: 20, marginRight: 10, color: returnStatusColor(changeRequestData.status) }}>•</Text>
                                                <Text medium align="right">{returnStatusText(changeRequestData.status)}</Text>
                                            </div>
                                        </ChangeRequestRow>
                                        {requestedChanges.length - 1 !== i ? <Divider /> : null}
                                    </React.Fragment>
                                ))
                            }
                            {
                                requestedChanges.length >= 50 && (
                                    <Button
                                        color="primary"
                                        customStyle={ styles.buttonStyle }
                                        onClick={loadMoreRequestedChanges}
                                        id="loadmorerequestedchanges"
                                    >
                                        {i18n.t(tokens.buttons.next)}
                                    </Button>
                                )
                            }
                        </div>
                    )
                    : (
                        <Text medium light align="left">{ i18n.t(tokens.labels.changeRequests.noChangeRequestsToDisplay) }</Text>
                    )
            }
        </>
    );
};

export default RequestedChangesTable;